import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Fade } from 'react-awesome-reveal';
import { PaymentFormV2 } from '../PaymentFormV2';
import { Button } from '../Button';
import { Icon } from '../Icon';
import { StepsV2, CHECKOUT_STEP } from '../StepsV2';
import CustomLink from '../CustomLink';
import { renderAgreementTerms } from '../../shared/render-agreement-terms';
import { usePaymentError } from '../../shared/use-payment-error-modal';
import { SUPPORT_EMAIL } from '../../shared/links';
import { logEvent } from '../../../lib/shared/event-logger';
import { EVENT_TYPES } from '../../../lib/shared/event-types';
import { getSubscriptionStatus } from '../../../lib/use-cases/get-subscription-status';
import checkoutImageSrc from '../../assets/images/slide/checkout-grandma-girl-tablet.png';
import styles from './CheckoutV4.css';

function CheckoutV4({ subscriptionData, merchantData, onClose, tryAgain, isPending, promoType, defaultDiscountValue = 50, successUrl, termsOfUseLink = '', isWalletEnabled = false, isPeriodInDays = false, legalEntity, submitLabel }) {
    const displayDiscount = subscriptionData.displayDiscountPercent ?? defaultDiscountValue;
    const isDiscountShown = Boolean(promoType) && displayDiscount > 0;
    const isTrialPeriodDifferent = Boolean(subscriptionData.trial) && (subscriptionData.trial.billingPeriod.value !== subscriptionData.billingPeriod.value);

    const [failedCard, setFailedCard] = useState(null);

    const fetchSubscriptionStatus = async () => {
        if (!merchantData.subscriptionId) return;

        const status = await getSubscriptionStatus(merchantData.subscriptionId);

        if (status?.card) {
            setFailedCard(status.card);
        }
    };

    useEffect(() => {
        document.getElementById('root').scrollIntoView({ block: 'start', behavior: 'smooth' });

        logEvent(EVENT_TYPES.SALE_CHECKOUT_PAGE_VISITED);
        fetchSubscriptionStatus();
    }, []);

    const { errorData, setErrorData, handleOnTryAgain } = usePaymentError(async () => {
        await tryAgain(subscriptionData, merchantData.subscriptionId);
        fetchSubscriptionStatus();
    });

    const renderTotalRow = () => {
        if (subscriptionData.trial?.billingPeriod) {
            const billingPeriodValue = subscriptionData.trial.billingPeriod.value;
            const planPeriodValue = subscriptionData.billingPeriod.value;

            return (
                <div className={styles.totalRow}>
                    <span>
                        After {billingPeriodValue} {subscriptionData.trial.billingPeriod.unit}{billingPeriodValue === 1 ? '' : 's'} (auto-renews
                        {isTrialPeriodDifferent && <> for {planPeriodValue}-{subscriptionData.billingPeriod.unit}{planPeriodValue === 1 ? '' : 's'} plan</>}
                        ):
                    </span>
                    <span>{subscriptionData.price}</span>
                </div>
            );
        }

        return (
            <div className={styles.totalRow}>
                <span>
                    After {subscriptionData.billingPeriod.value}{' '}
                    {subscriptionData.billingPeriod.unit}{subscriptionData.billingPeriod.value === 1 ? '' : 's'} (auto-renews):
                </span>
                <span>{subscriptionData.price}</span>
            </div>
        );
    };

    if (errorData.isModalVisible) {
        return (
            <Fade
                duration={500}
                triggerOnce
            >
                <div className={styles.root}>
                    <StepsV2 currentStepId={CHECKOUT_STEP} />
                    <div className={styles.content}>
                        <div className={styles.errorCard}>
                            <div className={styles.errorHeader}>
                                <h1 className={styles.title}>
                                    {errorData.title}
                                </h1>
                                <Button
                                    type='secondary'
                                    iconLeft={{ name: 'close' }}
                                    className={styles.buttonClose}
                                    onClick={onClose}
                                    buttonProps={{ 'aria-label': 'Close' }}
                                />
                            </div>
                            <div className={styles.noticeBlock}>
                                <Icon name='info' />
                                <p>{errorData.description}</p>
                            </div>
                            <div className={styles.helpBlock}>
                                <p>What to do:</p>
                                <p>{errorData.helpDescription}</p>
                            </div>
                            <div className={styles.buttons}>
                                <Button
                                    label='Try again'
                                    onClick={handleOnTryAgain}
                                    isLoading={isPending}
                                    type='ink'
                                />
                                <Button
                                    label='Decline'
                                    onClick={onClose}
                                    type='secondary'
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </Fade>
        );
    }

    return (
        <Fade
            duration={500}
            triggerOnce
        >
            <div className={styles.root}>
                <StepsV2 currentStepId={CHECKOUT_STEP} />
                <div className={styles.coupon}>
                    <div className={styles.couponContent}>
                        <Icon name='discountTag' className={styles.couponIcon} />
                        <p className={styles.couponText}>
                            {'The coupon code '}
                            <strong>welcome2026</strong>
                            {' has been applied. '}
                            <strong>Your discount of up to 42% is now active.</strong>
                            {' Enjoy your savings!'}
                        </p>
                    </div>
                </div>
                <div className={styles.content}>
                    <p className={styles.title}>Complete your checkout</p>
                    <div className={styles.summaryBlock}>
                        {isDiscountShown ? (
                            <>
                                {!isTrialPeriodDifferent && (
                                    <div className={styles.row}>
                                        <span>Regular {subscriptionData.summaryBillingPeriod.value}-{subscriptionData.summaryBillingPeriod.unit} price</span>
                                        <span>{subscriptionData.displayOldPrice ?? subscriptionData.summaryPrice}</span>
                                    </div>
                                )}
                                {!isTrialPeriodDifferent && (
                                    <div className={styles.row}>
                                        <span>Your {displayDiscount}% discount</span>
                                        <span className={styles.discountValue}>-{subscriptionData.discountPrice}</span>
                                    </div>
                                )}
                                {!isTrialPeriodDifferent && <div className={styles.divider} />}
                                <div className={styles.totalBlock}>
                                    <div className={styles.totalRow}>
                                        <span>Total today:</span>
                                        <span>{subscriptionData.trial?.price || subscriptionData.summaryPrice}</span>
                                    </div>
                                    {Boolean(subscriptionData.trial) && !isTrialPeriodDifferent && (
                                        <div className={styles.summarySaveBlock}>
                                            <Icon name='fire' />
                                            You save {subscriptionData.discountPrice} in first {subscriptionData.summaryBillingPeriod.value === 1 ? '' : subscriptionData.billingPeriod.value} {subscriptionData.billingPeriod.unit} ({displayDiscount}% OFF)
                                        </div>
                                    )}
                                </div>
                                {renderTotalRow()}
                            </>
                        ) : (
                            <>
                                <div className={styles.totalRow}>
                                    <span>Total today:</span>
                                    <span>{subscriptionData.trial?.price || subscriptionData.summaryPrice}</span>
                                </div>
                                <div className={styles.divider} />
                                {renderTotalRow()}
                            </>
                        )}
                        <p className={styles.agreementTermsText}>{renderAgreementTerms(subscriptionData, 'By selecting a payment method and proceeding', termsOfUseLink, isPeriodInDays)}</p>
                    </div>
                    <PaymentFormV2
                        merchantData={merchantData}
                        setErrorData={setErrorData}
                        successUrl={successUrl}
                        plan={subscriptionData.plan}
                        discount={promoType ? displayDiscount : 0}
                        failedCard={failedCard}
                        isWalletEnabled={isWalletEnabled}
                        submitLabel={submitLabel}
                        submitButtonType='pine'
                        hasSubmitArrow={false}
                    />
                    <div className={styles.memoirBlock}>
                        <p className={styles.memoirTitle}>Your digital book begins with one question</p>
                        <img src={checkoutImageSrc} alt='' className={styles.memoirImage} />
                    </div>
                    <p className={styles.copyright}>
                        {`©${new Date().getFullYear()} Boundly. All rights reserved.`}
                        <br />
                        {`${legalEntity.company} · ${legalEntity.address} · `}
                        <CustomLink to={`mailto:${SUPPORT_EMAIL}`} target='_blank'>{SUPPORT_EMAIL}</CustomLink>
                    </p>
                </div>
            </div>
        </Fade>
    );
}

CheckoutV4.propTypes = {
    subscriptionData: PropTypes.object.isRequired,
    merchantData: PropTypes.object.isRequired,
    onClose: PropTypes.func.isRequired,
    tryAgain: PropTypes.func.isRequired,
    isPending: PropTypes.bool.isRequired,
    promoType: PropTypes.string,
    defaultDiscountValue: PropTypes.number,
    successUrl: PropTypes.string,
    termsOfUseLink: PropTypes.string,
    isWalletEnabled: PropTypes.bool,
    isPeriodInDays: PropTypes.bool,
    legalEntity: PropTypes.shape({
        company: PropTypes.string,
        address: PropTypes.string
    }).isRequired,
    submitLabel: PropTypes.string
};

export {
    CheckoutV4
};
